import React, { useEffect, useState } from "react";
import { PixelStyle } from "../../components/layout";
import { HeaderContainer } from "./viewreport";
import TableLayout, { Td, Tr } from "../../components/tableLayout";
import { DeleteAllTourAPI, GetAllTourAPI, IsAssignedTourAPI } from "../../apis/tour";
import { useNavigate } from "react-router-dom";
import { FiEdit3 } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { ConfirmationModal } from "../../components/Modal";
import { usePagination } from "../../components/Pagination";
import Pagination from "../../components/Pagination";

const ViewTour = () => {
  const TableHead = [
    "Tour name",
    "Company name",
    "Location",
    "Status",
    "Action",
  ];
  const [tours, setTours] = useState([]);
  const [filteredTours, setFilteredTours] = useState([]);
  const [filters, setFilters] = useState({
    tourName: "",
    company: "",
    location: "",
    status: "",
  });
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTourId, setSelectedTourId] = useState(null);

  // Pagination
  const {
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filteredTours, 50);


  const navigate = useNavigate();

  const headerFieldMapping = {
    "Tour name": "tourName",
    "Company name": "companyName",
    Location: "checkpoints[0].Location", // Use first checkpoint's location
    Status: "status",
  };

  const FetchAllTours = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await GetAllTourAPI();
      const tourData = Array.isArray(response) ? response : [];
      
      setTours(tourData);
      setFilteredTours(tourData);
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch tours. Please try again.";
      setError(errorMessage);
      console.error("Error fetching tours:", error);
      setTours([]);
      setFilteredTours([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    FetchAllTours();
  }, []);

  const HandlerDelete = async (tourId) => {
    setSelectedTourId(tourId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedTourId) return;
    
    try {
      const response = await DeleteAllTourAPI(selectedTourId);
      if (response) {
        await FetchAllTours();
      }
    } catch (error) {
      console.error("Error deleting tour:", error);
      setError("Failed to delete tour. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setSelectedTourId(null);
    }
  };

  const handleEditClick = async (tourId) => {
    try {
      const response = await IsAssignedTourAPI(tourId);
      // Response structure: { statusCode, message, data: { tourId, isAssigned, assignedReports } }
      const isAssigned = response?.data?.isAssigned;
      if (isAssigned === true) {
        // Show confirmation popup if tour is assigned
        setSelectedTourId(tourId);
        setShowEditModal(true);
      } else {
        // Directly navigate to edit page if tour is not assigned
        confirmEdit(tourId);
      }
    } catch (error) {
      console.error("Error checking tour assignment status:", error);
      // On error, show the confirmation modal as a fallback
      setSelectedTourId(tourId);
      setShowEditModal(true);
    }
  };

  const confirmEdit = (tourId = null) => {
    // If tourId is an event object (from modal click), ignore it and use selectedTourId
    // Accept string or number IDs, but not objects
    const isValidId = tourId && (typeof tourId === 'string' || typeof tourId === 'number');
    const idToUse = isValidId ? tourId : selectedTourId;
    if (!idToUse) {
      console.error("No tour ID available");
      return;
    }
    navigate(`/viewtour/${idToUse}`);
    setShowEditModal(false);
    setSelectedTourId(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));

    // Apply all active filters
    const filtered = tours.filter((tour) => {
      const tourNameMatch = !filters.tourName || 
        (tour.tourName?.toLowerCase() || "").includes(filters.tourName.toLowerCase());
      
      const companyMatch = !filters.company || 
        (tour.companyName?.toLowerCase() || "").includes(filters.company.toLowerCase());
      
      // Fix location matching - use companyDetails instead of checkpoints
      const locationString = tour.companyDetails 
        ? `${tour.companyDetails.address || ""}, ${tour.companyDetails.city || ""}, ${tour.companyDetails.state || ""} ${tour.companyDetails.zipCode || ""}`
        : "";
      const locationMatch = !filters.location || 
        locationString.toLowerCase().includes(filters.location.toLowerCase());
      
      const statusMatch = !filters.status || 
        (tour.status?.toLowerCase() || "").includes(filters.status.toLowerCase());

      // Apply the specific filter that was just changed
      if (name === "tourName") {
        const newTourNameMatch = !value || (tour.tourName?.toLowerCase() || "").includes(value.toLowerCase());
        return newTourNameMatch && companyMatch && locationMatch && statusMatch;
      }
      if (name === "company") {
        const newCompanyMatch = !value || (tour.companyName?.toLowerCase() || "").includes(value.toLowerCase());
        return tourNameMatch && newCompanyMatch && locationMatch && statusMatch;
      }
      if (name === "location") {
        const newLocationMatch = !value || locationString.toLowerCase().includes(value.toLowerCase());
        return tourNameMatch && companyMatch && newLocationMatch && statusMatch;
      }
      if (name === "status") {
        const newStatusMatch = !value || (tour.status?.toLowerCase() || "").includes(value.toLowerCase());
        return tourNameMatch && companyMatch && locationMatch && newStatusMatch;
      }
      
      return tourNameMatch && companyMatch && locationMatch && statusMatch;
    });

    setFilteredTours(filtered);
  };

  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setSortField(field);
    setSortOrder(newOrder);

    const sorted = [...filteredTours].sort((a, b) => {
      let aValue, bValue;
      
      // Handle location field - use companyDetails instead of checkpoints
      if (field.includes("checkpoints") || field === "Location") {
        const aLocation = a.companyDetails 
          ? `${a.companyDetails.address || ""}, ${a.companyDetails.city || ""}, ${a.companyDetails.state || ""} ${a.companyDetails.zipCode || ""}`
          : "";
        const bLocation = b.companyDetails 
          ? `${b.companyDetails.address || ""}, ${b.companyDetails.city || ""}, ${b.companyDetails.state || ""} ${b.companyDetails.zipCode || ""}`
          : "";
        aValue = aLocation;
        bValue = bLocation;
      } else {
        aValue = a[field] || "";
        bValue = b[field] || "";
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return newOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return newOrder === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredTours(sorted);
  };

  const toggleSortOrder = (header) => {
    const field = headerFieldMapping[header];
    if (field) {
      handleSort(field);
    }
  };

  return (
    <div className={`w-full text-sm h-full ${PixelStyle}`}>
      <HeaderContainer pageTitle="View Tour" isRedirecting={false}>
        <span className="text-black">View Tours</span>
      </HeaderContainer>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded m-6">{error}</div>
      )}

      <div className="flex justify-between items-center w-full px-5 py-2">
        <h4 className="text-sm font-semibold">View Tours</h4>
        {/* <button
          className="text-zinc-500 p-2 rounded flex items-center gap-2"
          onClick={() =>
            ExportToExcel(ExportPayload(reportTemplates), "View report")
          }
        >
          <GoDownload className="text-xl" />
        </button> */}
      </div>
      <div className="bg-white rounded-2xl shadow-lg overflow-auto">
        {loading ? (
          <div className="p-6 text-center">Loading tours...</div>
        ) : (
          <TableLayout
            TableHead={TableHead}
            sortField={sortField}
            sortOrder={sortOrder}
            toggleSortOrder={toggleSortOrder}
            headerFieldMapping={headerFieldMapping}
            showPagination={false}
          >
            <>
              <tr className="border-b bg-zinc-50 text-xs">
                {["tourName", "company", "location", "status"].map(
                  (header, index) => (
                    <th scope="col" className="px-6 py-1" key={index}>
                      <input
                        placeholder={`Filter by ${
                          header === "tourName"
                            ? "Tour name"
                            : header === "company"
                            ? "Company name"
                            : header === "location"
                            ? "Location"
                            : "Status"
                        }`}
                        name={header}
                        value={filters[header]}
                        onChange={handleFilterChange}
                        className="border p-1 px-2 rounded-md font-normal outline-blue-500 w-full"
                      />
                    </th>
                  )
                )}
                <th></th>
              </tr>
              {paginatedData.map((tour) => (
                <Tr key={tour?.id} className="border-b py-2">
                  <Td>{tour?.tourName}</Td>
                  <Td>{tour?.companyName}</Td>
                  <Td>
                    {tour?.companyDetails 
                      ? `${tour.companyDetails.address || ""}, ${tour.companyDetails.city || ""}, ${tour.companyDetails.state || ""} ${tour.companyDetails.zipCode || ""}`.trim()
                      : "N/A"}
                  </Td>
                  <Td>{tour?.status || "N/A"}</Td>
                  <Td>
                    <button
                      className="text-blue-500 text-sm p-2 hover:bg-blue-100 rounded-md"
                      onClick={() => handleEditClick(tour?.id)}
                    >
                      <FiEdit3 size={16} />
                    </button>
                    <button
                      className="text-red-500 text-sm p-2 hover:bg-red-100 rounded-md"
                      onClick={() => HandlerDelete(tour?.id)}
                    >
                      <MdDeleteOutline size={16} />
                    </button>
                  </Td>
                </Tr>
              ))}
            </>
          </TableLayout>
        )}
      </div>

      {/* Pagination - Outside shadow card */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onItemsPerPageChange={handleItemsPerPageChange}
        className="mt-4"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTourId(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Tour"
        message="Tour is assigned to a report. Are you sure you would like to delete this tour?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Edit Confirmation Modal */}
      <ConfirmationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTourId(null);
        }}
        onConfirm={confirmEdit}
        title="Edit Tour"
        message="Tour is assigned to a report. Are you sure you would like to edit this tour?"
        confirmText="Edit"
        cancelText="Cancel"
        confirmVariant="primary"
      />
    </div>
  );
};

export default ViewTour;
